// aiChatClient.ts

import { SpaceInfo } from "../../../types/plan";
import { SpacesName } from "../stores/aiChatStore";

/**
 * Represents a single message in the chat history.
 */
export interface ChatMessage {
  uuid: string | undefined;
  role: "user" | "assistant" | "system"; // System role might be included in history from backend potentially
  content: string;
  rawContent?: string;
  result?: NewSearchResult; // Optional: Link search results directly to the message
  requestId?: string;
  metadata?: {
    searchMode: "global" | "current";
    selectedSpaces?: string[];
    planInfo?: {
      propertyName: string;
      planName: string;
    };
    timestamp?: string;
  };
}

/**
 * Structure of the request body sent to the /api/ai/chat endpoint.
 */
export interface ChatRequestBody {
  userPrompt: string;
  messageHistory?: ChatMessage[];
  spacesNames: SpacesName[];
}

// --- AI Response Action Types (based on system_prompt.md) ---

export interface ProximitySearchParameters {
  target_type: string; // e.g., "women_apparel_store"
  max_distance: number | null;
  distance_unit: string; // e.g., "feet", "meters"
  is_within: boolean;
  availability: string; // e.g., "available", "any"
}

export interface ProximityCriteria {
  target_type: string;
  max_distance: number | null;
  distance_unit: string;
  is_close: boolean;
}

export interface PriceCriteria {
  rent_type: string; // e.g., "per_square_foot", "monthly"
  min_price: number | null;
  max_price: number | null;
  currency: string; // e.g., "USD", "EUR"
}

export interface MultiCriteriaSearchParameters {
  proximity_criteria: ProximityCriteria;
  price_criteria: PriceCriteria;
  availability: string;
}

export interface LeaseCriteria {
  lease_status: string; // e.g., "ending_soon", "long_term"
  min_months_remaining: number | null;
  max_months_remaining: number | null;
}

export interface ExpandableSearchParameters {
  proximity_criteria: {
    // Specific structure for expandable search
    target_type: string;
    adjacency: string; // e.g., "next_to", "near"
  };
  lease_criteria: LeaseCriteria;
  expansion_potential: boolean;
  availability: string;
}

export interface CustomSearchParameters {
  // Define specific parameters if known, otherwise use a flexible type
  [key: string]: any;
}

export interface MissingInfoItem {
  field: string;
  description: string;
  options?: string[]; // Optional list of possible values
}

export interface RequestMoreInfoResponse {
  action_type: "request_more_info";
  missing_info: MissingInfoItem[];
  partial_understanding: {
    // Define structure if known, otherwise use a flexible type
    [key: string]: any;
  };
  description: string;
}

/**
 * Discriminated union representing the possible structured responses from the AI.
 * Based on the 'action_type' field.
 */
export type AIActionResponse =
  | {
      action_type: "proximity_search";
      parameters: ProximitySearchParameters;
      description: string;
    }
  | {
      action_type: "multi_criteria_search";
      parameters: MultiCriteriaSearchParameters;
      description: string;
    }
  | {
      action_type: "expandable_search";
      parameters: ExpandableSearchParameters;
      description: string;
    }
  | {
      action_type: "custom_search";
      parameters: CustomSearchParameters;
      description: string;
    }
  | RequestMoreInfoResponse; // Already includes action_type: 'request_more_info'

/**
 * Structure of a successful response from the /api/ai/chat endpoint.
 */
export interface ChatSuccessResponse {
  aiResponse: AIActionResponse | null; // Structured AI response
  rawResponse: string; // Raw text response from AI
}

// Define the structure for search results
export interface SearchResult {
  id: string; // Unique identifier for the result item
  propertyNumber: string;
  tenantName: string;
  gla: string;
  spaceNumber: string;
  icon?: string; // Optional icon path or identifier
}

export interface SearchResultSpaceInfo {
  // 10.81.6
  totalSales: string;
  totalSalesPSF: string;
  spaceID: string | undefined;
  area: number;
  tenant: string | undefined;
  distance: number;
  totalRentPSF: string;
  leaseEndDate: string;
  totalRent: string;
  mall_name?: string; // Optional mall/property name
  property_name?: string; // Alternative field for property name
  plan_name?: string; // Plan name for switching plans feature
  property_number?: string; // Property number for switching properties/malls
  highlight_color?: string; // Optional hex color override from agent response (e.g. "#0000FF")
}

export interface NewSearchResult {
  targetPlanInfo: SpaceInfo | undefined;
  searchResults: SearchResultSpaceInfo[];
}

/**
 * Structure for error responses from the backend API.
 */
export interface BackendErrorResponse {
  error: string;
  details?: string;
}

/**
 * Calls the backend AI chat API endpoint.
 *
 * @param apiUrl - The full URL of the chat endpoint (e.g., 'http://localhost:3000/api/ai/chat').
 * @param userPrompt - The user's current message.
 * @param messageHistory - An array of previous messages in the conversation.
 * @returns A promise that resolves with the ChatSuccessResponse object.
 * @throws An error if the API call fails or returns an error status.
 */
export async function callChatApi(
  apiUrl: string,
  userPrompt: string,
  messageHistory: ChatMessage[] = [],
  spacesNames: SpacesName[] = [],
): Promise<ChatSuccessResponse> {
  const requestBody: ChatRequestBody = {
    userPrompt,
    messageHistory,
    spacesNames,
  };

  try {
    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json", // Explicitly accept JSON
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse = {
        error: `HTTP error! Status: ${response.status}`,
      };
      try {
        // Try to parse backend error response
        errorData = await response.json();
      } catch (e) {
        // Ignore if parsing fails, use the basic HTTP error message
        console.warn("Could not parse error response body as JSON.");
      }
      // Combine error messages if details are available
      const errorMessage = errorData.details
        ? `${errorData.error} - Details: ${errorData.details}`
        : errorData.error;
      throw new Error(errorMessage);
    }

    const data: ChatSuccessResponse = await response.json();

    // Basic validation of the successful response structure
    if (
      !data ||
      typeof data.aiResponse !== "object" ||
      typeof data.rawResponse !== "string"
    ) {
      throw new Error("Invalid response structure received from the API.");
    }

    return data;
  } catch (error) {
    console.error("Error calling chat API:", error);
    // Re-throw the error to be handled by the caller
    // Ensure it's an Error object
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("An unknown error occurred during the API call.");
    }
  }
}
